import { Component, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { CartService } from './shared/services/cart.service';
import { CompareService } from './shared/services/compare.service';
import { WishlistService } from './shared/services/wishlist.service';
import { NavigationEnd, Router } from '@angular/router';
import { CultureService } from './shared/culture/culture.service';
import { DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import { CurrencyService } from './shared/services/currency.service';
import { debounceTime, takeUntil, takeWhile } from 'rxjs/operators';
import { interval, Subject, timer } from 'rxjs';
import { UserService } from './shared/services/user.service';
import { StoreService } from './shared/services/store.service';
import { QuickviewService } from './shared/services/quickview.service';
import { StorageService } from './shared/services/storage.service';
import { AnalyticsService } from './shared/services/analytics.service';
import { ThemeService } from './shared/services/theme.service';
import { GoogleAuthService } from './shared/services/google-auth.service';
import { CountryGateService } from './shared/services/country-gate.service';
import { environment } from 'src/environments/environment';
import { installPickers } from './shared/pickers';
import { VisitSourceService } from './shared/services/visit-source.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    cookieAccepted:boolean=true;

    /// Set once this page has an answer from Locale/me and from Locale/me2. The five-second check
    /// went by cookies alone, and those are set by responses from www - a page opened on
    /// the bare domain never sees them - so every open tab asked both endpoints every five seconds
    /// for as long as it stayed open, spending the country lookups every other visitor needed.
    private countryKnown = false;
    private visitRecorded = false;
    private destroy$ = new Subject<void>();
    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private router: Router,
        private toastr: ToastrService,
        private cart: CartService,
        private user:UserService,
        private store:StoreService,
        private compare: CompareService,
        private wishlist: WishlistService,
        private zone: NgZone,
        private scroller: ViewportScroller,
        private visitSource: VisitSourceService,
        private quickView:QuickviewService,
        // Held here only so it exists from the first page: it marks the owner's and sellers'
        // browsers with a cookie the server reads before deciding on a country redirect.
        private countryGate: CountryGateService,
        private currency: CurrencyService, 
        private storage:StorageService,
        private analytics: AnalyticsService,
        // Held here only so it exists from the first page: it paints the site's theme (Theme_Spec.md).
        private theme: ThemeService,
        private googleAuth: GoogleAuthService,
        private renderer: Renderer2, @Inject(DOCUMENT) private _document: any,
        private culture: CultureService,
    ) {
        // A page in a language search engines may not index yet says so, on the server render (Multilang_Culture_Spec.md 2.3).
        this.culture.watch(this.router);
        // The visitor's country, for the language suggestion (5.6): never a redirect, only an offer.
        this.store.onCountry.pipe(takeUntil(this.destroy$)).subscribe(info => this.visitorCountry = info && !info.Pending ? info.CountryCode || '' : '');
    }

    visitorCountry = '';
    readonly inBrowser = isPlatformBrowser(this.platformId);
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void 
    {  
        if (isPlatformBrowser(this.platformId))
        {
            // Where this visit came from (ChatGPT, Google, Facebook...), kept for a shop application to carry.
            this.visitSource.capture();
            // Our own select lists and date/time panels in the theme's colours, for every field on the site.
            this.zone.runOutsideAngular(() => installPickers(this._document, (key, fallback) => this.culture.t(key, fallback)));
            // Before tracking starts, so the landing page view already carries the account.
            // The profile is restored from storage synchronously, so this has it on first emit.
            this.user.onUserInfoChanged$.pipe(takeUntil(this.destroy$)).subscribe(info => {
                this.analytics.setUser(info && info.Id);
            });

            this.cart.init();
            this.zone.runOutsideAngular(() => {
                // Asked outside Angular, answered inside it: the prices on screen change to the visitor's currency
                // at once, not at their next click. Unanswered, the page stops waiting and shows the United States;
                // the retries below may still find the visitor's own country.
                this.store.getCountryInfo()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(res => this.zone.run(() => {
                        this.countryKnown = true;
                        this.store.setCountryInfo(res);
                    }), () => this.zone.run(() => this.store.fallBackToDefaultCountry()));
                this.injectScripts();
                // this.router.events.pipe(filter(event => event instanceof NavigationEnd), first()).subscribe(() => {   }); 
                setTimeout(() => 
                { 
                    if(localStorage.getItem('cookie_accept') === "true"){
                        this.cookieAccepted = true;
                    }
                    else{
                        this.cookieAccepted = false;
                    }
                    try{
                        const cookies = document.cookie.split('; ');
                        const cookie = cookies.find((row) => row.startsWith(`${'me2_security'}=`));
                        var res = cookie ? cookie.split('=')[1] : null;
                        if(res != 'done' && !this.visitRecorded)
                        {
                            this.store.getMe2(this.router.url).pipe(takeUntil(this.destroy$)).subscribe(res =>
                            {
                                this.visitRecorded = true;
                            });
                        }
                    }
                    catch{}

                }, 9000);
            });

            // The theme's sample promo popup (a "50% off" picture, quickview's #testmodal) is not PrimeHat's: it is never opened.

            // Retries only until both have answered, then stops.
            timer(10000, 5000)
            .pipe(takeUntil(this.destroy$), takeWhile(() => !(this.countryKnown && this.visitRecorded)))
            .subscribe(() => {
                this.checkCountryStat();
            });

            // The rate a tab shows is never over an hour old: asked again every ten minutes (the server refreshes on the
            // same beat) and checked every minute, so if it cannot be asked, prices go back to US dollars within a
            // minute of the rate turning an hour old rather than being shown at an old rate.
            timer(60 * 1000, 60 * 1000)
            .pipe(takeUntil(this.destroy$))
            .subscribe(minute => {
                if ((minute + 1) % 10 === 0) {
                    this.store.getCountryInfo().pipe(takeUntil(this.destroy$)).subscribe(
                        res => this.store.setCountryInfo(res),
                        () => { /* the check below turns an old rate into dollars */ });
                }
                const current = this.currency.options;
                if (current && current.code && current.code !== this.currency.site.code && !StoreService.rateIsFresh(this.store.countryInfo)) {
                    this.store.setCountryInfo(StoreService.inSiteCurrency(this.store.countryInfo));
                }
            });
        }

        this.store.onCountry.pipe(takeUntil(this.destroy$)).subscribe(res =>
        {
            // A fresh rate, or the site's own currency: an answer whose rate is over an hour old is shown in that.
            const info = res && res.CurrencyMultiplier > 0 ? res : this.store.getCountryInfoFromStorage();
            CurrencyService.useSite(info);
            const shown = StoreService.rateIsFresh(info) ? info : StoreService.inSiteCurrency(info);
            this.store.countryInfo = shown;
            const decimals = shown.CurrencyDecimals === 0 || shown.CurrencyDecimals === 3 ? shown.CurrencyDecimals : 2;
            this.currency.options = {
                code: shown.CurrencyCode || this.currency.site.code,
                display: shown.CurrencySymbol || this.currency.site.symbol,
                digitsInfo: `1.${decimals}-${decimals}`,
                locale: 'en-US',
                multiplier: shown.CurrencyMultiplier > 0 ? shown.CurrencyMultiplier : 1,
                decimals,
                // Only while the prices are in it: a rate turned an hour old shows dollars here and on the owner's screens.
                ownerPick: shown.OwnerCurrency && shown.OwnerCurrency === shown.CurrencyCode ? shown.OwnerCurrency : '',
            };
        });

        this.router.events.pipe(
            takeUntil(this.destroy$),
            debounceTime(100)
            ).subscribe((event) => {
            // A shop's tab keeps its own place (ShopLayoutComponent.followTabClicks): its state rides in history.state.
            const state = typeof history !== 'undefined' ? history.state : null;
            if (event instanceof NavigationEnd && !(state && state.shopTab)) {
                this.scroller.scrollToPosition([0, 0]);
            }
        });

        this.cart.onAdding$.pipe(takeUntil(this.destroy$)).subscribe(res => 
        {
            if(res && res !== null && res.ID > 0)
            {
                this.toastr.success(this.culture.t('cart-added-name', '"{name}" added to cart!', { name: res.Name }), this.culture.t('added', 'Added'), {timeOut:4000, closeButton:true});
            }
            else if(res && res.MaxExceed == true)
            {
                this.toastr.error(this.culture.t('cart-max-quantity-reached', 'Max quantity reached'), this.culture.t('limit-reached', 'Limit Reached'), {timeOut:4000, closeButton:true});
            }
            else
            {
                this.toastr.error(this.culture.t('cart-add-failed', 'Couldn\'t add to cart, try again!'), this.culture.t('failed', 'Failed'), {timeOut:4000, closeButton:true});
            }
        });

        this.compare.onAdding$.pipe(takeUntil(this.destroy$)).subscribe(product => {
            this.toastr.success(this.culture.t('compare-added-name', '"{name}" added to compare!', { name: product.Name }), this.culture.t('added', 'Added'), {timeOut:4000, closeButton:true});
        });

        this.wishlist.onAdding$.pipe(takeUntil(this.destroy$)).subscribe(product =>
        {
            this.toastr.success(this.culture.t('wishlist-added-name', '"{name}" added to wish list!', { name: product.Name }), this.culture.t('added', 'Added'), {timeOut:4000, closeButton:true});
        });
        this.user.getSetUserAddress().subscribe();
    }

    cookieAccept()
    {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('cookie_accept','true');
            this.cookieAccepted = true;
            this.analytics.grantConsent();
        }
    }

    injectScripts()
    {
        // Google Analytics 4 for environment.analytics.measurementId, and page views for every
        // router navigation - none while environment.analytics has no measurement id. No Tag Manager
        // container loads (index.html): PrimeHat's own, if it gets one, goes there.
        this.analytics.init();

        // Collects a Google sign-in that had to leave the page because the popup was
        // blocked. Costs nothing when there is none in flight: it reads one
        // sessionStorage key and only then loads the auth chunk.
        this.googleAuth.completeRedirectIfAny();
    }

    isOlderThanDays(dateString: string, days: number): boolean {
        const storedDate = new Date(dateString);
        const currentDate = new Date();
        const diffTime = currentDate.getTime() - storedDate.getTime();
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        return diffDays > days;
    }

    checkCountryStat()
    {
        if (isPlatformBrowser(this.platformId) && environment.production == true)
        {
            const cookies = document.cookie.split('; ');
            const cookie = cookies.find((row) => row.startsWith(`${'me2_security'}=`));
            var res = cookie ? cookie.split('=')[1] : null;

            const cookie2 = cookies.find((row) => row.startsWith('cname='));
            const cname = cookie2 ? cookie2.split('=')[1] : null;

            if(res == 'done') { this.visitRecorded = true; }
            if(cname) { this.countryKnown = true; }

            if(!this.visitRecorded)
            {
                this.store.getMe2(this.router.url).pipe(takeUntil(this.destroy$)).subscribe(res =>
                {
                    this.visitRecorded = true;
                });
            }

            if(!this.countryKnown)
            {
                this.store.getCountryInfo()
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(res => {
                        this.countryKnown = true;
                        this.store.setCountryInfo(res);
                });
            }
        } 
    } 
}  